import { cn } from '@/lib/utils';
import { CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { useEffect, useMemo, useRef, useState } from 'react';

export interface OpcionMulti {
  value: string;
  label: string;
}

/**
 * Filtro de selección múltiple: botón con resumen + panel con buscador y
 * casillas. Selección vacía = "todas" (sin filtro).
 */
export function MultiSelect({
  opciones,
  seleccion,
  onChange,
  todasLabel,
  className,
}: {
  opciones: OpcionMulti[];
  seleccion: string[];
  onChange: (valores: string[]) => void;
  /** Texto cuando no hay nada seleccionado, p. ej. "Todas las partidas". */
  todasLabel: string;
  className?: string;
}) {
  const [abierto, setAbierto] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', fuera);
    return () => document.removeEventListener('mousedown', fuera);
  }, [abierto]);

  const sel = useMemo(() => new Set(seleccion), [seleccion]);
  const visibles = useMemo(() => {
    const t = q.trim().toLowerCase();
    return t ? opciones.filter((o) => o.label.toLowerCase().includes(t)) : opciones;
  }, [opciones, q]);

  const resumen =
    seleccion.length === 0
      ? todasLabel
      : seleccion.length === 1
        ? (opciones.find((o) => o.value === seleccion[0])?.label ?? '1 seleccionada')
        : `${seleccion.length} seleccionadas`;

  function alternar(v: string) {
    const next = new Set(sel);
    if (!next.delete(v)) next.add(v);
    onChange([...next]);
  }

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setAbierto((a) => !a)}
        className="flex h-8 w-56 items-center justify-between gap-2 rounded-md border border-border bg-white px-3 text-xs"
        aria-expanded={abierto}
      >
        <span className={cn('truncate', seleccion.length === 0 && 'text-muted-foreground')}>
          {resumen}
        </span>
        <CaretDown className="h-3.5 w-3.5 shrink-0" weight="bold" aria-hidden="true" />
      </button>
      {abierto && (
        <div className="absolute left-0 z-50 mt-1 w-72 rounded-md border border-border bg-white p-2 shadow-lg">
          <div className="relative mb-2">
            <MagnifyingGlass
              className="-translate-y-1/2 absolute top-1/2 left-2 h-3.5 w-3.5 text-muted-foreground"
              weight="bold"
              aria-hidden="true"
            />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar…"
              className="h-7 w-full rounded border border-border pr-2 pl-7 text-xs"
            />
          </div>
          <div className="mb-1 flex items-center justify-between text-[11px]">
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() =>
                onChange([...new Set([...seleccion, ...visibles.map((o) => o.value)])])
              }
            >
              Seleccionar visibles
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => onChange([])}
            >
              Limpiar
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {visibles.map((o) => (
              <label
                key={o.value}
                className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-zinc-100"
              >
                <input
                  type="checkbox"
                  checked={sel.has(o.value)}
                  onChange={() => alternar(o.value)}
                />
                <span className="min-w-0 flex-1 truncate" title={o.label}>
                  {o.label}
                </span>
              </label>
            ))}
            {visibles.length === 0 && (
              <p className="px-1.5 py-2 text-xs text-muted-foreground">Sin resultados.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
